import './AssembledProductPage.scss';

import { b2x } from '@b2x/react/src';
import React from 'react';

import { AssembledComponentSkuTile } from '../AssembledComponentSkuTile';
import { Breadcrumb } from '../Breadcrumb';
import { Button } from '../Button';
import { Container } from '../Container';
import { t } from '../i18n/i18n';
import { Icon } from '../Icon';
import { ProductsSlider } from '../slider/ProductsSlider';
import { Page } from './Page';
import { AttributesSection, ProductGallery, ProductMediaContent, ProductPrice } from './ProductPage';

export interface AssembledProductPageProps {}

export const AssembledProductPage = (props: AssembledProductPageProps) => {
  const _product = b2x.useProduct({ populate: b2x.appConfig.api?.productPopulate });

  const { addToCart, assembledProduct, product } = b2x.useProductAssembler({
    lonelySkuAutomaticAddition: true,
    preselectDefaultComponent: true,
    productId: _product?.id,
  });

  const { session } = b2x.useAppContext();

  const currentBreakpoint = b2x.useBreakpoint();

  return (
    <Page
      className="assembled-product-page pb-0 pb-lg-5"
      noPaddingBottom
      noPaddingTop
      thingsToLoadBeforePageReady={[assembledProduct]}
    >
      {product !== undefined && assembledProduct && (
        <b2x.ProductContext product={product}>
          {(productContext) => {
            const price = productContext.selectedProductVariant.skus?.at(0)?.price;

            return (
              <React.Fragment>
                <b2x.structuredData.Product product={product} selectedSku={productContext.selectedSku} />
                <section className="product-first-section py-lg-5 mb-3 mb-lg-0 bg-white">
                  <Container>
                    {b2x.untilBreakpoint('md', currentBreakpoint) && (
                      <b2x.Col size={12}>
                        <b2x.Div paddingTop={3}>
                          <Breadcrumb />
                        </b2x.Div>
                      </b2x.Col>
                    )}
                    <b2x.Row>
                      <b2x.Col size={{ lg: 6, xs: 12 }}>
                        <div className="position-relative">
                          <ProductGallery
                            product={product}
                            selectedProductVariant={productContext.selectedProductVariant}
                            selectedSku={productContext.selectedSku}
                          />
                        </div>
                      </b2x.Col>
                      <b2x.Col size={{ lg: 6, xs: 12 }}>
                        <div className="ps-lg-5">
                          <b2x.Div marginBottom={4}>
                            {!b2x.untilBreakpoint('md', currentBreakpoint) && (
                              <b2x.Div className="border-bottom" marginBottom={2} paddingBottom={1}>
                                <Breadcrumb />
                              </b2x.Div>
                            )}
                            {productContext.selectedProductVariant.name && (
                              <b2x.H1 className="product-title display-1 fw-bold" marginBottom={1}>
                                {b2x.formatHtml(productContext.selectedProductVariant.name)}
                              </b2x.H1>
                            )}
                            {productContext.selectedProductVariant.descriptionShort && (
                              <b2x.H3 className="fw-light text-uppercase" marginBottom={1}>
                                {b2x.formatHtml(productContext.selectedProductVariant.descriptionShort)}
                              </b2x.H3>
                            )}
                            {productContext.selectedSku?.name && (
                              <b2x.H6 className="fw-light text-uppercase mb-3">
                                {productContext.selectedSku.name}
                              </b2x.H6>
                            )}
                            {productContext.selectedSku?.measurement && !product.assembled && (
                              <p className="small mb-2 mb-md-4">
                                {b2x.formatHtml(
                                  t('productPage.measurement', {
                                    measurementUnit:
                                      productContext.selectedSku.measurement.measurementUnit?.toLowerCase(),
                                    value: `<span class="fw-light">${productContext.selectedSku.measurement.value}</span>`,
                                  })
                                )}
                              </p>
                            )}
                          </b2x.Div>
                          {assembledProduct.assembledComponents?.find(
                            (assembledComponent) =>
                              assembledComponent.componentSkus && assembledComponent.componentSkus.length > 0
                          ) && (
                            <b2x.Div marginBottom={{ lg: 5, xs: 3 }}>
                              {assembledProduct.assembledComponents.map((assembledComponent) => {
                                const selectedSku = assembledComponent.componentSkus?.find(
                                  (componentSku) => componentSku.quantity === 1
                                )?.sku;

                                return (
                                  <React.Fragment key={assembledComponent.id}>
                                    {assembledComponent.componentSkus &&
                                      assembledComponent.componentSkus.length > 1 && (
                                        <b2x.Div marginBottom={3}>
                                          <b2x.Div
                                            marginBottom={
                                              assembledComponent.componentSkus.length &&
                                              assembledComponent.componentSkus.length > 0
                                                ? 2
                                                : undefined
                                            }
                                          >
                                            <span>{`${assembledComponent.name}: `}</span>
                                            {selectedSku?.name ? (
                                              <span className="fw-bold">{selectedSku.name}</span>
                                            ) : (
                                              <small className="text-red">
                                                {t('misc.assembledProduct.selectComponent')}
                                              </small>
                                            )}
                                          </b2x.Div>
                                          {assembledComponent.componentSkus.length &&
                                            assembledComponent.componentSkus.length > 0 && (
                                              <b2x.Div className="product-skus">
                                                <b2x.SwiperContext>
                                                  {({ navigationNextElRef, navigationPrevElRef }) => {
                                                    return (
                                                      <b2x.Div className="slider-container">
                                                        <Button
                                                          aria-label={t('accessibility.button.swiper_prev')}
                                                          className="prev"
                                                          iconStart={{ name: 'light-arrow-left' }}
                                                          innerRef={navigationPrevElRef}
                                                          justifyContent="start"
                                                          variant="blank"
                                                        />
                                                        <b2x.Swiper
                                                          navigation={{ custom: true }}
                                                          slides={assembledComponent.componentSkus?.map(
                                                            (componentSku) => (
                                                              <AssembledComponentSkuTile
                                                                assembledComponent={assembledComponent}
                                                                assembledProduct={product}
                                                                componentSku={componentSku}
                                                                key={componentSku.code}
                                                              />
                                                            )
                                                          )}
                                                          slidesPerView={'auto'}
                                                          spaceBetween={8}
                                                        />
                                                        <Button
                                                          aria-label={t('accessibility.button.swiper_next')}
                                                          className="next"
                                                          iconStart={{ name: 'light-arrow-right' }}
                                                          innerRef={navigationNextElRef}
                                                          justifyContent="end"
                                                          variant="blank"
                                                        />
                                                      </b2x.Div>
                                                    );
                                                  }}
                                                </b2x.SwiperContext>
                                              </b2x.Div>
                                            )}
                                        </b2x.Div>
                                      )}
                                  </React.Fragment>
                                );
                              })}
                            </b2x.Div>
                          )}

                          {price && (
                            <ProductPrice
                              priceHelper={{
                                crossedOutPrice: price.crossedOutValue,
                                discountPercentage: price.discountPercentage,
                                isPriceInRange: false,
                                price: price.value,
                              }}
                            />
                          )}

                          {/* <ProductPrice
                            priceHelper={{
                              crossedOutPrice: assembledProduct.totalCrossedOutPrice,
                              isPriceInRange: false,
                              price: assembledProduct.totalPrice,
                            }}
                          /> */}

                          {product.description && (
                            <b2x.Div className="fw-light" marginBottom={{ lg: 5, xs: 3 }}>
                              {b2x.formatHtml(product.description)}
                            </b2x.Div>
                          )}

                          <b2x.Div marginBottom={{ md: 4, xs: 3 }}>
                            {!assembledProduct.completed && (
                              <div className="text-red mb-2 small">{t('misc.assembledProduct.selectAllComponent')}</div>
                            )}
                            <Button
                              disabled={!assembledProduct.completed}
                              onClick={addToCart}
                              variant="outline-primary"
                            >
                              <React.Fragment>
                                <Icon name="plus" size={12} />
                                {t('form.addToCartForm.buttons.submit.label')}
                              </React.Fragment>
                            </Button>
                          </b2x.Div>

                          {assembledProduct.completed && (
                            <div className="my-3">
                              <b2x.InstallmentPaymentBanner amount={assembledProduct.totalPrice} />
                            </div>
                          )}

                          <AttributesSection
                            attributesToShow={['BSK_COSA_FA', 'BSK_MODO_USO', 'BSK_INGREDIENTI']}
                            product={productContext.selectedProductVariant}
                            sku={productContext.selectedSku}
                          />

                          {session?.cart?.shippingProfile?.freeThreshold && (
                            <p className="small">
                              {b2x.formatHtml(
                                t('product.freeShippingFrom', {
                                  freeShippingFromAmount: b2x.formatCurrency(
                                    session.cart.shippingProfile.freeThreshold
                                  ),
                                })
                              )}
                            </p>
                          )}
                        </div>
                      </b2x.Col>
                    </b2x.Row>
                  </Container>
                </section>
                <ProductMediaContent />
                <b2x.RelatedProducts productId={productContext.selectedProductVariant.id}>
                  {({ fetching, relatedProducts }) =>
                    fetching ? (
                      <b2x.Loading />
                    ) : (
                      relatedProducts &&
                      relatedProducts.length > 0 && (
                        <section className="product-third-section">
                          <b2x.Container fluid="always">
                            <div className="bg-white px-3 px-lg-5 py-5 overflow-hidden">
                              <b2x.P
                                className={'text-uppercase letter-spacing-sm'}
                                fontWeight={700}
                                marginBottom={4}
                                textAlign={'center'}
                              >
                                {t('product.relatedProduct.title')}
                              </b2x.P>
                              <b2x.Listing
                                name="Product page - Related products"
                                products={relatedProducts}
                                sendEvent={false}
                              >
                                <ProductsSlider fullWidth products={relatedProducts} />
                              </b2x.Listing>
                            </div>
                          </b2x.Container>
                        </section>
                      )
                    )
                  }
                </b2x.RelatedProducts>
              </React.Fragment>
            );
          }}
        </b2x.ProductContext>
      )}
    </Page>
  );
};
